<script lang="ts" setup>
import type { InputTypeState } from '~kui/composables/useInputState';
import type { SizeVariants } from '~kui/types/kui';

import 'intl-tel-input/build/css/intlTelInput.css';
import type { Iti } from 'intl-tel-input';

import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { getInputStateClassesV2 } from '~kui/composables/useInputState';
import { INPUT_STATE_DEFAULT, INPUT_STATE_DISABLED, INPUT_STATE_ERROR, INPUT_STATE_VALID } from '~kui/constants/kui';
import intlTelInput from 'intl-tel-input';
import { onBeforeUnmount, onMounted, ref } from 'vue';

// Props
interface Props {
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  initialCountry?: string;
  onlyCountries?: string[];
  excludeCountries?: string[];
  separateDialCode?: boolean;
  disabled?: boolean;
  allowDropdown?: boolean;
  required?: boolean;
  verified?: boolean;
  containerRounded?: Extract<SizeVariants, 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'> | 'full' | 'none';
  useFullScreenPopup?: boolean;
  version?: 'v1' | 'v2';
  inputTypeState?: InputTypeState;
}

const props = withDefaults(defineProps<Props>(), {
  hideLabel: false,
  placeholder: 'Enter phone number',
  initialCountry: 'auto',
  onlyCountries: () => [],
  excludeCountries: () => [],
  separateDialCode: true,
  disabled: false,
  allowDropdown: false,
  required: false,
  verified: false,
  containerRounded: 'none',
  useFullScreenPopup: true,
  version: 'v1',
  inputTypeState: undefined,
});

const emit = defineEmits<{
  'valid': [isValid: boolean];
  'keydown.enter': [];
}>();

const phoneNumberValue = defineModel<string>({ required: true });

const inputState = ref<'disabled' | 'error' | 'valid' | 'default'>(props.disabled ? INPUT_STATE_DISABLED : INPUT_STATE_DEFAULT);

const { wrapperClasses, elementClasses, fontClasses } = getInputStateClassesV2({
  disabled: computed(() => props.disabled),
  state: computed(() => props.inputTypeState),
});

// Refs
const phoneInput = ref<HTMLInputElement | null>(null);
let iti: Iti | null = null;

const intlTelInputOptsContainer = ref<HTMLDivElement | undefined>(undefined);

// Handle input changes
const handleInput = (event: Event) => {
  if (!event.isTrusted) {
    return;
  }
  if (phoneInput.value && iti) {
    const rawInput = phoneInput.value.value.trim();
    phoneNumberValue.value = rawInput ? iti.getNumber() : '';

    if (props.disabled) {
      inputState.value = INPUT_STATE_DISABLED;
      return;
    }
    // Validate if utils are loaded
    if (iti.isValidNumber) {
      const isValid = iti.isValidNumber();
      if (isValid !== null) {
        if (isValid === false) {
          inputState.value = INPUT_STATE_ERROR;
          emit('valid', false);
        }
        if (isValid === true) {
          inputState.value = INPUT_STATE_VALID;
          emit('valid', true);
        }
      }
    }
  }
};

const containerClasses = computed(() => {
  if (props.version === 'v2') {
    return 'h-full w-full';
  }
  return twMerge(
    cva('', {
      variants: {
        containerRounded: {
          'none': '',
          'sm': 'rounded-sm',
          'md': 'rounded-md',
          'lg': 'rounded-lg',
          'xl': 'rounded-xl',
          '2xl': 'rounded-2xl',
          '3xl': 'rounded-3xl',
          '4xl': 'rounded-4xl',
          'full': 'rounded-full',
        },
      },
    })({
      containerRounded: props.containerRounded,
    }),
  );
});

// Initialize intl-tel-input
onMounted(async () => {
  if (phoneInput.value) {
    const options: Record<string, unknown> = {
      initialCountry: props.initialCountry,
      separateDialCode: props.separateDialCode,
      allowDropdown: props.allowDropdown,
      loadUtils: () => import('intl-tel-input/utils'),
      countrySearch: false,
      containerClass: containerClasses.value,
      useFullscreenPopup: props.useFullScreenPopup, // be predictable
      fixDropdownWidth: !props.useFullScreenPopup,
    };

    if (props.useFullScreenPopup) {
      options.dropdownContainer = intlTelInputOptsContainer.value;
    }

    // Only add array options if they have values
    if (props.onlyCountries && props.onlyCountries.length > 0) {
      options.onlyCountries = props.onlyCountries;
    }
    if (props.excludeCountries && props.excludeCountries.length > 0) {
      options.excludeCountries = props.excludeCountries;
    }

    iti = intlTelInput(phoneInput.value, options);

    if (phoneNumberValue.value) {
      iti.setNumber(phoneNumberValue.value);
    }
    await nextTick();
  }
});

onBeforeUnmount(() => {
  if (iti) {
    iti.destroy();
  }
});

const classList = computed(() => {
  return twMerge(
    props.version === 'v2' ? '' : 'placeholder:text-muted-foreground border-box inline-flex w-full items-center  rounded-lg border-1 bg-white text-base leading-none outline-none data-disabled:cursor-not-allowed',
    cva('w-full p-4', {
      variants: {
        inputState: {
          [INPUT_STATE_ERROR]: 'border-destructive',
          [INPUT_STATE_VALID]: 'border-success-foreground',
          [INPUT_STATE_DEFAULT]: 'border-input',
          [INPUT_STATE_DISABLED]: 'border-input bg-muted/50 text-muted-foreground',
        },
      },
    })({
      inputState: inputState.value,
    }),
  );
});

const id = useId();

const handleKeydownEnter = () => {
  // eslint-disable-next-line vue/custom-event-name-casing
  emit('keydown.enter');
};
</script>

<template>
  <div class="w-full relative" :class="version === 'v2' ? 'group/kui-input' : ''">
    <KUICoreInputLabel
      v-if="!hideLabel"
      :required="required"
      :label="label"
    />

    <div class="relative w-full">
      <div v-if="version === 'v2'" :class="wrapperClasses">
        <input
          :id="id"
          ref="phoneInput"
          type="tel"
          :placeholder="props.placeholder"
          :disabled="props.disabled"
          :class="[fontClasses, elementClasses]"
          @input="handleInput($event)"
          @keydown.enter="handleKeydownEnter"
        >
      </div>
      <input
        v-else
        :id="id"
        ref="phoneInput"
        type="tel"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :class="classList"
        @input="handleInput($event)"
        @keydown.enter="handleKeydownEnter"
      >
      <Icon
        v-if="verified"
        :name="ICONS.checkmarkCircle"
        size="lg"
        class="text-success-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-10"
      />
    </div>
    <div ref="intlTelInputOptsContainer" />
  </div>
</template>

<style>
/* cannot scope the style, and cannot avoid this */
/* magic javascript makes this element, not me! */
.iti {
  width: 100%;
}
</style>
